<template>
    <app-layout title="Settings">
        <page-header>
            <template #title>Settings - {{ printer.name }}</template>

            <template #actions>
                <jet-button type="button" class="h-7 leading-none" @click="editPrinter">
                    Edit
                </jet-button>
                <jet-tertiary-button type="button" class="h-7 leading-none" :href="route('settings.printers.index')">
                    Close
                </jet-tertiary-button>
            </template>
        </page-header>

        <settings-navigation />

        <div class="px-8 space-y-8 pb-10">
            <hr class="border-gray-200">

            <div class="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2 text-sm">
                <div class="grid gap-x-3 gap-y-1" style="grid-template-columns: max-content 1fr;">
                    <b>Name:</b>
                    <span>{{ printer.name }}</span>
                    <b>Queue Name:</b>
                    <span>{{ printer.queue_name }}</span>
                    <b>Model:</b>
                    <span>{{ printer.model }}</span>
                </div>
                <div class="grid gap-x-3 gap-y-1" style="grid-template-columns: max-content 1fr;">
                    <b>Divisions:</b>
                    <span>{{ printer.divisions_label }}</span>
                    <b>Initial Labels:</b>
                    <span>{{ printer.initial_labels_label }}</span>
                    <b>IP Address:</b>
                    <span>{{ printer.ip_address }}</span>
                </div>
                <div class="grid gap-x-3 gap-y-1" style="grid-template-columns: max-content 1fr;">
                    <b>Status:</b>
                    <span>{{ printer.active ? 'Online' : 'Offline' }}</span>
                    <b>Exclusive:</b>
                    <span>{{ printer.exclusive ? 'Yes' : 'No' }}</span>
                    <b>Label:</b>
                    <span>{{ labelSummary }}</span>
                </div>
            </div>

            <div>
                <h3 class="text-lg font-semibold mb-2">Claims</h3>
                <div class="rounded-t overflow-hidden border border-b-0">
                    <table class="table w-full">
                        <thead>
                            <tr>
                                <th class="text-left">Checker</th>
                                <th class="text-left">Held Since</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="!claims.length">
                                <td colspan="3" class="text-left text-gray-500">No active claims</td>
                            </tr>
                            <tr v-for="claim in claims" :key="claim.id">
                                <td class="text-left">{{ claim.checker?.name }}</td>
                                <td class="text-left">{{ formatDateTime(claim.assigned_at) }}</td>
                                <td class="text-right">
                                    <jet-secondary-button type="button" class="leading-none py-1 px-2" @click="releaseClaim(claim)">
                                        Release
                                    </jet-secondary-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div>
                <h3 class="text-lg font-semibold mb-2">Queue</h3>
                <div class="rounded-t overflow-hidden border border-b-0">
                    <table class="table w-full">
                        <thead>
                            <tr>
                                <th class="text-left">Time</th>
                                <th class="text-left">Order #</th>
                                <th class="text-left">Label</th>
                                <th class="text-left">Checker</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="!queue.length">
                                <td colspan="5" class="text-left text-gray-500">No pending jobs</td>
                            </tr>
                            <tr v-for="job in queue" :key="job.id">
                                <td class="text-left">{{ formatDateTime(job.created_at) }}</td>
                                <td class="text-left">{{ job.order_id }}</td>
                                <td class="text-left">{{ job.is_initial_label ? 'Initial' : '' }}</td>
                                <td class="text-left">{{ job.checker?.name }}</td>
                                <td class="text-right">
                                    <jet-secondary-button type="button" class="leading-none py-1 px-2" @click="cancelJob(job)">
                                        Cancel
                                    </jet-secondary-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p v-if="queueRemainder > 0" class="text-sm text-gray-600 mt-2">
                    Showing oldest 50 of {{ queueTotal }}. {{ queueRemainder }} more in queue.
                </p>
            </div>

            <div>
                <h3 class="text-lg font-semibold mb-2">History</h3>
                <div class="rounded-t overflow-hidden border border-b-0">
                    <table class="table w-full">
                        <thead>
                            <tr>
                                <th class="text-left">Time</th>
                                <th class="text-left">Order #</th>
                                <th class="text-left">Label</th>
                                <th class="text-left">Checker</th>
                                <th class="text-left">Printed By</th>
                                <th class="text-left">Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="job in history.data" :key="job.id">
                                <td class="text-left">{{ formatDateTime(job.created_at) }}</td>
                                <td class="text-left">{{ job.order_id }}</td>
                                <td class="text-left">{{ job.is_initial_label ? 'Initial' : '' }}</td>
                                <td class="text-left">{{ job.checker?.name }}</td>
                                <td class="text-left">{{ job.user?.name }}</td>
                                <td class="text-left">{{ job.status }}</td>
                                <td class="text-right">
                                    <jet-secondary-button
                                        v-if="job.status === 'Completed'"
                                        type="button"
                                        class="leading-none py-1 px-2"
                                        @click="reprintJob(job)"
                                    >
                                        Reprint
                                    </jet-secondary-button>
                                    <jet-secondary-button
                                        v-else-if="job.status === 'Cancelled' || job.status === 'Failed'"
                                        type="button"
                                        class="leading-none py-1 px-2"
                                        @click="retryJob(job)"
                                    >
                                        Retry
                                    </jet-secondary-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex justify-between items-center mt-4">
                    <pagination class="mt-0 mb-0" :links="history.links" />
                    <div class="w-40">
                        <multiselect
                            v-model.number="historyPaginate"
                            :options="paginateOptions"
                            :canDeselect="false"
                            :canClear="false"
                            :classes="{ singleLabel: 'w-full px-2' }"
                            class="h-8 multi-sm multi-custom text-base"
                            openDirection="top"
                            @change="reloadHistory"
                        />
                    </div>
                </div>
            </div>
        </div>

        <printer-form-modal
            :show="showPrinterModal"
            :mode="showPrinterModal"
            :form="form"
            :initial-by-division="initialByDivision"
            :active-counts-by-division="activeCountsByDivision"
            :show-delete-modal="showDeleteModal"
            @close="closePrinterModal"
            @submit="submitForm"
            @delete-confirm="showDeleteModal = true"
            @delete-cancel="showDeleteModal = false"
            @delete="handleDeletePrinter"
        />
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import { Inertia } from '@inertiajs/inertia';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import PrinterFormModal from '@/Pages/Settings/Partials/PrinterFormModal';
import Pagination from '@/Jetstream/Pagination';
import Multiselect from '@vueform/multiselect';

import '@vueform/multiselect/themes/default.css';

export default {
    setup(props) {
        const form = useForm({
            id: null,
            name: '',
            queue_name: '',
            model: '',
            ip_address: '',
            label_width: '',
            label_height: '',
            unprintable_margin: '',
            divisions: [],
            initial_label_divisions: [],
            exclusive: false,
            is_mobile: false,
            active: true,
        });

        return { form };
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetSecondaryButton,
        JetTertiaryButton,
        SettingsNavigation,
        PrinterFormModal,
        Pagination,
        Multiselect,
    },

    props: [
        'printer',
        'claims',
        'queue',
        'queueTotal',
        'history',
        'paginate',
        'initialByDivision',
        'activeCountsByDivision',
    ],

    data() {
        return {
            showPrinterModal: false,
            showDeleteModal: false,
            historyPaginate: this.paginate || 20,
            paginateOptions: [
                { value: 10, label: 'Show 10' },
                { value: 20, label: 'Show 20' },
                { value: 50, label: 'Show 50' },
                { value: 100, label: 'Show 100' },
            ],
        };
    },

    computed: {
        queueRemainder() {
            return Math.max(0, (this.queueTotal || 0) - (this.queue?.length || 0));
        },

        labelSummary() {
            const width = this.printer.label_width;
            const height = this.printer.label_height;
            const margin = this.printer.unprintable_margin;
            if (width == null && height == null && margin == null) {
                return '';
            }
            const size = [width, height].every((v) => v != null && v !== '')
                ? `${Number(width).toFixed(2)} × ${Number(height).toFixed(2)} in`
                : '';
            const marginPart = margin != null && margin !== ''
                ? `${Number(margin).toFixed(2)} margin`
                : '';
            return [size, marginPart].filter(Boolean).join(', ');
        },
    },

    methods: {
        formatDateTime(value) {
            if (!value) return '';
            return new Date(value).toLocaleString();
        },

        editPrinter() {
            const data = this.printer;
            this.form.id = data.id;
            this.form.name = data.name;
            this.form.queue_name = data.queue_name;
            this.form.model = data.model || '';
            this.form.ip_address = data.ip_address || '';
            this.form.label_width = data.label_width ?? '';
            this.form.label_height = data.label_height ?? '';
            this.form.unprintable_margin = data.unprintable_margin ?? '';
            this.form.divisions = [...(data.division_codes || [])];
            this.form.initial_label_divisions = [...(data.initial_label_divisions || [])];
            this.form.exclusive = !!data.exclusive;
            this.form.is_mobile = !!data.is_mobile;
            this.form.active = !!data.active;
            this.form.clearErrors();
            this.showPrinterModal = 'edit';
        },

        closePrinterModal() {
            this.form.reset();
            this.form.clearErrors();
            this.showPrinterModal = false;
            this.showDeleteModal = false;
        },

        submitForm() {
            const payload = {
                ...this.form.data(),
                label_width: this.form.label_width === '' ? null : this.form.label_width,
                label_height: this.form.label_height === '' ? null : this.form.label_height,
                unprintable_margin: this.form.unprintable_margin === '' ? null : this.form.unprintable_margin,
                model: this.form.model || null,
                ip_address: this.form.ip_address || null,
                divisions: this.form.divisions || [],
                initial_label_divisions: this.form.initial_label_divisions || [],
            };

            this.form.transform(() => payload).put(this.route('settings.printers.update', this.form.id), {
                onSuccess: () => this.closePrinterModal(),
            });
        },

        handleDeletePrinter() {
            this.form.delete(this.route('settings.printers.destroy', this.form.id), {
                onSuccess: () => this.closePrinterModal(),
                onError: () => { this.showDeleteModal = false; },
            });
        },

        releaseClaim(claim) {
            Inertia.post(route('settings.printers.assignments.release', [this.printer.id, claim.id]));
        },

        cancelJob(job) {
            Inertia.post(route('settings.printers.jobs.cancel', [this.printer.id, job.id]));
        },

        reprintJob(job) {
            Inertia.post(route('settings.printers.jobs.reprint', [this.printer.id, job.id]));
        },

        retryJob(job) {
            Inertia.post(route('settings.printers.jobs.retry', [this.printer.id, job.id]));
        },

        reloadHistory() {
            Inertia.get(route('settings.printers.show', this.printer.id), {
                paginate: this.historyPaginate,
            }, { preserveState: true });
        },
    },
};
</script>
